import CheckInput from 'assets/components/inputs/check/CheckInput';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import { getLocales } from 'assets/locales/Locale';
import { defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { isValidTimeSpan } from 'assets/utils/parsersAndValidation/Validators';
import { isNil } from 'lodash';
import { AutomationInputTypes, ExpressionTypes } from 'models/enums/Automation';
import { AutomationRuleSchema, getAutomationExpressionValueInputType } from '../helpers/AutomationTypes';
import {
  getPropertyMetadata,
  isExpressionValueMissing,
  isAnyExpression,
  isRangeExpression,
  parseContainsExpressionValue,
  parseRangeValue,
  serializeContainsExpressionValue,
  serializeRangeValue,
} from '../helpers/ruleHelpers';

type Props = {
  expression: Model.Automation.IExpression;
  schema: AutomationRuleSchema;
  readonly?: boolean;
  onChangeValue(value: string): void;
};

const timeSpanFormat = '[-][d.]hh:mm:ss[.fffffff]';

export default function AutomationExpressionValueInput(props: Props) {
  const { lang } = getLocales();
  const metadata = getPropertyMetadata(props.schema, props.expression.propertyPath);
  const inputType = getAutomationExpressionValueInputType(props.expression.type);
  const isAny = isAnyExpression(props.expression);
  const isContains = props.expression.type === ExpressionTypes.Contains;
  const isRange = isRangeExpression(props.expression);
  const disabled = props.readonly || !props.expression.type;
  const requiredError = isExpressionValueMissing(props.expression) ? lang.required : undefined;
  const propertyOptions = props.schema.expressionProperties.map((propertyPath) => ({
    id: propertyPath,
    title: propertyPath,
  }));

  if (inputType === AutomationInputTypes.Json || isAny) return null;

  if (inputType === AutomationInputTypes.Number) {
    return (
      <NumberInput
        name="value"
        step={0.01}
        value={props.expression.value === '' ? undefined : Number(props.expression.value)}
        error={requiredError}
        readOnly={disabled}
        disabled={disabled}
        onChange={({ value }) => props.onChangeValue(value?.toString?.() ?? '')}
      />
    );
  }

  if (inputType === AutomationInputTypes.Text) {
    return (
      <TextInput
        name="value"
        value={props.expression.value || ''}
        error={requiredError}
        readOnly={disabled}
        disabled={disabled}
        onChange={({ value }) => props.onChangeValue(value || '')}
      />
    );
  }

  if (inputType === AutomationInputTypes.Boolean) {
    return (
      <CheckInput
        name="value"
        value={props.expression.value || 'false'}
        primaryState="true"
        secondaryState="false"
        label="state"
        readOnly={disabled}
        disabled={disabled}
        onChange={({ value }) => {
          if (!disabled) props.onChangeValue(value?.toString?.() ?? 'false');
        }}
      />
    );
  }

  if (inputType === AutomationInputTypes.DateTime) {
    return (
      <DateInput
        name="value"
        value={props.expression.value}
        dateTimeFormat={defaultServerDateTimeFormat}
        error={requiredError}
        readOnly={disabled}
        disabled={disabled}
        onChange={({ value }) => props.onChangeValue(value || '')}
      />
    );
  }

  if (inputType === AutomationInputTypes.TimeSpan) {
    return (
      <TextInput
        name="value"
        value={props.expression.value || ''}
        placeholder={timeSpanFormat}
        error={
          requiredError ||
          (!isValidTimeSpan(props.expression.value) && lang.invalidFormatRequires.replace('{format}', timeSpanFormat))
        }
        readOnly={disabled}
        disabled={disabled}
        onChange={({ value }) => props.onChangeValue(value || '')}
      />
    );
  }

  if (inputType === AutomationInputTypes.Property) {
    return (
      <SelectInput
        name="value"
        data={propertyOptions}
        value={props.expression.value}
        error={requiredError}
        readOnly={disabled}
        disabled={disabled}
        onChange={({ value }) => props.onChangeValue(value || '')}
      />
    );
  }

  if (isRange) {
    const rangeValue = parseRangeValue(props.expression.value);
    return (
      <>
        {lang.between}
        <NumberInput
          name="minValue"
          step={0.01}
          value={rangeValue.min}
          readOnly={props.readonly}
          disabled={props.readonly}
          onChange={({ minValue }) => props.onChangeValue(serializeRangeValue({ min: minValue, max: rangeValue.max }))}
        />
        {lang.and}
        <NumberInput
          name="maxValue"
          step={0.01}
          value={rangeValue.max}
          readOnly={props.readonly}
          disabled={props.readonly}
          onChange={({ maxValue }) => props.onChangeValue(serializeRangeValue({ min: rangeValue.min, max: maxValue }))}
        />
      </>
    );
  }

  const parsedValue = isContains
    ? parseContainsExpressionValue(metadata, props.expression.value)
    : metadata?.parseValue
      ? metadata.parseValue(props.expression.value)
      : props.expression.value?.toString();
  const serializeValue = (value: any) =>
    isContains
      ? serializeContainsExpressionValue(metadata, value)
      : (metadata?.serializeValue?.(value) ?? value?.toString?.() ?? '');

  if (metadata?.inputType === AutomationInputTypes.DateTime) {
    return (
      <DateInput
        name="value"
        value={parsedValue}
        dateTimeFormat={defaultServerDateTimeFormat}
        error={requiredError}
        readOnly={disabled}
        disabled={disabled}
        onChange={({ value }) => props.onChangeValue(serializeValue(value))}
      />
    );
  }

  if (metadata?.inputType === AutomationInputTypes.Select) {
    return (
      <SelectInput
        name="value"
        data={metadata.options || []}
        value={parsedValue}
        error={requiredError}
        readOnly={disabled}
        disabled={disabled}
        onChange={({ value }) => {
          metadata.onValueChange?.(value);
          props.onChangeValue(serializeValue(value));
        }}
      />
    );
  }

  if (metadata?.inputType === AutomationInputTypes.Number) {
    return (
      <NumberInput
        name="value"
        step={0.01}
        value={parsedValue === '' || isNil(parsedValue) ? undefined : Number(parsedValue)}
        error={requiredError}
        readOnly={disabled}
        disabled={disabled}
        onChange={({ value }) =>
          props.onChangeValue(
            isContains ? serializeContainsExpressionValue(metadata, value) : (value?.toString?.() ?? '')
          )
        }
      />
    );
  }

  return (
    <TextInput
      name="value"
      value={parsedValue?.toString()}
      error={requiredError}
      readOnly={disabled}
      disabled={disabled}
      onChange={({ value }) =>
        props.onChangeValue(
          isContains ? serializeContainsExpressionValue(metadata, value) : (value?.toString?.() ?? '')
        )
      }
    />
  );
}
